'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { fetchCapabilityMatrix, isCapabilityAllowed } from '@/lib/capabilities';
import type { CapabilityMatrix } from '@/lib/capabilities';

// ─── Impersonation types ────────────────────────────────────────────────────
export interface ImpersonatedUser {
  id: string;
  name: string;
  email: string;
  role: 'client' | 'staff' | 'superadmin';
  org: string;
}

const IMPERSONATION_KEY = 'celsoft_impersonation';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState<CapabilityMatrix>({});
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const supabase = createClient();

  /** Re-fetch the capability matrix from Supabase and update state. */
  const refreshCapabilities = useCallback(async () => {
    setCapabilitiesLoading(true);
    try {
      const matrix = await fetchCapabilityMatrix();
      setCapabilities(matrix);
    } finally {
      setCapabilitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // Clear impersonation on real auth change (e.g. logout)
      if (!session) {
        setImpersonatedUser(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem(IMPERSONATION_KEY);
        }
      }
    });

    // Restore impersonation from localStorage on mount
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(IMPERSONATION_KEY);
        if (stored) setImpersonatedUser(JSON.parse(stored));
      } catch { /* ignore */ }
    }

    // Fetch capabilities on mount (background service)
    refreshCapabilities();

    return () => subscription.unsubscribe();
  }, []);

  /** Start impersonating a user — super admin only. */
  const startImpersonation = useCallback((target: ImpersonatedUser) => {
    setImpersonatedUser(target);
    if (typeof window !== 'undefined') {
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(target));
    }
  }, []);

  /** Stop impersonating and return to super admin session. */
  const stopImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(IMPERSONATION_KEY);
    }
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  /**
   * Check whether the current user (by role) can access a page path,
   * using the live capability matrix loaded from Supabase.
   */
  const checkCapability = (path: string, role: string | null | undefined): boolean => {
    return isCapabilityAllowed(capabilities, path, role);
  };

  const value = {
    user,
    session,
    loading,
    capabilities,
    capabilitiesLoading,
    refreshCapabilities,
    checkCapability,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    // Impersonation
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
